import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { CATEGORY_FOR_KIND, type Category, type Mood, type Need, type Orientation, type Place, type Source, type SourceDefaults, type SourceKind } from "@instead/shared";
import { api } from "../lib/api.ts";
import { moodLabel, useT, type T } from "../lib/i18n.ts";
import { catLabel } from "./DealCard.tsx";
import { Ic } from "./Icons.tsx";
import { CategoryRows } from "./ItemForm.tsx";
import { NeedChips } from "./NeedChips.tsx";
import { ChipGroup, Sheet } from "./Ui.tsx";

/**
 * Per-source import defaults (feedback F7b, D-36): the item form's facets,
 * chosen once for a list. The category starts on the kind rule (Videos for
 * YouTube, Listening for podcasts and music, Articles for feeds) and is
 * editable; nothing here is required. Whatever is chosen reaches only the
 * items the source adds from then on — never anything already on the shelf.
 */

/** The category the kind rule would pick — shown pre-selected, and what "unset" means. */
export function kindCategory(cats: Category[], kind: SourceKind): Category | undefined {
  return cats.find((c) => !c.parentId && c.kind === CATEGORY_FOR_KIND[kind]);
}

export function SourceDefaultsEditor({ value, onChange, kind }: { value: SourceDefaults; onChange: (v: SourceDefaults) => void; kind: SourceKind }) {
  const t = useT();
  const [sheet, setSheet] = useState<"category" | "where" | null>(null);
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });
  const { data: catData } = useQuery({ queryKey: ["categories"], queryFn: api.categories.list });
  const { data: moodsData } = useQuery({ queryKey: ["moods"], queryFn: api.moods.list });
  const { data: placesData } = useQuery({ queryKey: ["places"], queryFn: api.places.list, enabled: settings?.features.context ?? false });
  const cats = catData?.categories ?? [];
  const places = placesData?.places ?? [];
  const whereOn = settings?.features.context ?? false;
  const needsOn = settings?.features.needs ?? false;
  const set = (patch: Partial<SourceDefaults>) => onChange({ ...value, ...patch });

  const effectiveCat = value.categoryId ? cats.find((c) => c.id === value.categoryId) : kindCategory(cats, kind);
  const parent = effectiveCat?.parentId ? cats.find((c) => c.id === effectiveCat.parentId) : undefined;
  const catText = effectiveCat ? (parent ? `${catLabel(parent, t)} › ${effectiveCat.name}` : catLabel(effectiveCat, t)) : t("add.choose");
  const whereKind = value.whereKind ?? "anywhere";
  const place = places.find((p) => p.id === value.placeId);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
      <div className="field-row">
        <span className="label" id="sd-cat-label">{t("add.category")}</span>
        <button type="button" className="picker" aria-labelledby="sd-cat-label" onClick={() => setSheet("category")}>
          <span className="grow">{catText}</span>{Ic.down({ size: 14 })}
        </button>
      </div>

      {(moodsData?.moods.length ?? 0) > 0 && (
        <div>
          <div className="eyebrow">{t("rest.feel")}</div>
          <ChipGroup multi label={t("rest.feel")} selected={value.moods ?? []} options={(moodsData?.moods ?? []).map((m) => ({ value: m.key, label: moodLabel(m) }))} onChange={(moods) => set({ moods })} />
        </div>
      )}

      <div>
        <div className="eyebrow">{t("add.about")}</div>
        <ChipGroup label={t("add.about")} selected={value.orientation ? [value.orientation] : []}
          options={(["create", "consume", "be"] as const).map((o) => ({ value: o, label: t(`orient.${o}`) }))}
          onChange={(v) => set({ orientation: (v[0] as Orientation | undefined) ?? null })} />
      </div>

      {whereOn && (
        <div>
          <div className="eyebrow">{t("add.where")}</div>
          <div className="chip-row" role="group" aria-label={t("add.where")}>
            {(["anywhere", "indoor", "outdoor"] as const).map((k) => (
              <button key={k} type="button" className="chip" aria-pressed={whereKind === k} onClick={() => set({ whereKind: k, placeId: null })}>{t(`where.${k}`)}</button>
            ))}
            <button type="button" className="chip" aria-pressed={whereKind === "place"} onClick={() => setSheet("where")}>
              {whereKind === "place" && place ? place.name : t("where.place")}
            </button>
          </div>
        </div>
      )}

      {needsOn && (
        <div>
          <div className="eyebrow">{t("add.needs")}</div>
          <NeedChips label={t("add.needs")} selected={value.needIds ?? []} onChange={(needIds) => set({ needIds })} allowCreate={false} />
        </div>
      )}

      <label className="check-row">
        <input type="checkbox" checked={value.tracksProgress ?? true} onChange={(e) => set({ tracksProgress: e.target.checked })} />
        {t("add.trackProgress")}
      </label>

      {sheet === "category" && (
        <Sheet title={t("add.category")} onClose={() => setSheet(null)}>
          {cats.filter((c) => !c.parentId).map((c) => (
            <CategoryRows key={c.id} c={c} cats={cats} selected={effectiveCat?.id ?? ""} onPick={(id) => { set({ categoryId: id }); setSheet(null); }} />
          ))}
        </Sheet>
      )}

      {sheet === "where" && (
        <Sheet title={t("add.where")} onClose={() => setSheet(null)}>
          {places.map((p) => (
            <button key={p.id} type="button" className="sheet-row" onClick={() => { set({ whereKind: "place", placeId: p.id }); setSheet(null); }}>
              <span style={{ color: "var(--accent)" }}>{Ic.place({ size: 16 })}</span>
              <span className="grow">{p.name}</span>
              {value.placeId === p.id && Ic.check({ size: 16 })}
            </button>
          ))}
          {places.length === 0 && <p className="meta">{t("places.new")}</p>}
        </Sheet>
      )}
    </div>
  );
}

/** One short line for the Sources list: "Hobbies · Calming · take in · Kitchen table". */
export function summarizeDefaults(
  d: SourceDefaults | null,
  v: { cats: Category[]; moods: Mood[]; places: Place[]; needs: Need[] },
  t: T
): string {
  if (!d) return "";
  const parts: string[] = [];
  const cat = d.categoryId ? v.cats.find((c) => c.id === d.categoryId) : undefined;
  if (cat) parts.push(cat.parentId ? cat.name : catLabel(cat, t));
  for (const key of d.moods ?? []) { const m = v.moods.find((x) => x.key === key); if (m) parts.push(moodLabel(m)); }
  if (d.orientation) parts.push(t(`orient.${d.orientation}`));
  if (d.whereKind === "place") { const p = v.places.find((x) => x.id === d.placeId); if (p) parts.push(p.name); }
  else if (d.whereKind && d.whereKind !== "anywhere") parts.push(t(`where.${d.whereKind}`));
  for (const id of d.needIds ?? []) { const n = v.needs.find((x) => x.id === id); if (n) parts.push(n.name); }
  if (d.tracksProgress === false) parts.push(t("sources.defaultsNoProgress"));
  return parts.join(" · ");
}

/** Change a source's defaults after the fact — future items only, said in one line. */
export function SourceDefaultsSheet({ source, onClose, onSaved }: { source: Source; onClose: () => void; onSaved: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [value, setValue] = useState<SourceDefaults>(source.defaults ?? {});
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (defaults: SourceDefaults | null) => api.sources.update(source.id, { defaults }),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["sources"] }); onSaved(); onClose(); },
    onError: (e) => setError(e.message),
  });
  return (
    <Sheet title={t("sources.defaultsTitle", { name: source.label })} onClose={onClose}>
      <p className="meta" style={{ marginBottom: "var(--sp-3)", whiteSpace: "normal" }}>{t("sources.defaultsFuture")}</p>
      <SourceDefaultsEditor value={value} onChange={setValue} kind={source.kind} />
      {error && <div role="alert" className="banner banner-danger" style={{ marginTop: "var(--sp-2)" }}><span>{error}</span></div>}
      <div className="dialog-actions" style={{ marginTop: "var(--sp-3)" }}>
        {source.defaults && <button type="button" className="btn" disabled={save.isPending} onClick={() => save.mutate(null)}>{t("sources.defaultsClear")}</button>}
        <button type="button" className="btn" onClick={onClose}>{t("cancel")}</button>
        <button type="button" className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate(value)}>{t("save")}</button>
      </div>
    </Sheet>
  );
}
